// One guest's RIFT session lifecycle: connect with the participant token,
// publish the uplink on flow 1, play flow 2, report RTT on flow 3, answer the
// gateway's keyframe requests on flow 4, and keep doing that across network
// drops and token expiry without a page refresh.
//
// An admitted guest stays connected whether or not the producer has put them
// on a slot (contracts v2 §6): the gateway forwards their uplink only while
// they are routed, so a take is a route change, not a connect. When the
// gateway routes them it sends `{"kind":"keyframeRequest"}` on flow 4 and the
// next video frame is encoded as an IDR, so the site's decoder does not wait
// out a GOP.
//
// Reconnect is the acceptance criterion of JSS#749: browser RIFT was retired
// from the JBS preview plane because a post-connect WebTransport drop froze
// tiles until a manual refresh. Here every way a session can end — the
// transport closing (nextEvent → null), a thrown error, the gateway asking
// for a drain, RTT probes going silent, the token nearing expiry — funnels
// into the same loop: fetch a token if needed, connect again, resume. The
// encoders keep running throughout; a new session gets a fresh keyframe.
//
// A session the gateway or the network ends is not redialled blind. The
// gateway closes a removed guest's session (close code 4003) and refuses
// their Hello (4001) until their token expires, but rift-wt does not surface
// WebTransport close codes: a close is just the end of `nextEvent()`. So
// before redialling, the client asks the caller once whether the guest is
// still admitted (`confirmAdmitted`, one read of its own row). If not, the
// client ends. If there is no answer, it redials as before, because losing
// the network is not a Leave. A session the gateway closes before
// Established is a refused Hello. While the row still says admitted, the
// client retries `maxRefusals` times in a row (a key rotation can refuse
// once), then gives up rather than retrying forever.
//
// Token refresh is break-then-make: the new token is fetched while the old
// session is still live, then the old session is shut down and the new one
// dialled at once. The gateway replaces a participant's browser session when
// a second one arrives (contracts §2.3), so make-before-break buys nothing
// but a moment of two sessions uploading the same camera; the gap is one
// handshake and a keyframe.
import { RateController } from './rate-control.js';
import { SeqReorder } from './reorder.js';
import { FLOW_CONTROL, FLOW_RETURN, FLOW_RTT, } from './session.js';
/** Flow-4 payloads are JSON, at most 1 KiB (contracts v2 §2.2). */
export const MAX_CONTROL_BYTES = 1024;
/** Parse a flow-4 payload; null when it is oversized, not JSON, or has no `kind`. */
export function parseControlMessage(payload) {
    if (payload.byteLength > MAX_CONTROL_BYTES)
        return null;
    try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg && typeof msg === 'object' && typeof msg.kind === 'string') {
            return msg;
        }
    }
    catch {
        /* not JSON */
    }
    return null;
}
const defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));
export class GuestClient {
    o;
    state = 'idle';
    stopped = false;
    session = null;
    endSession = null;
    rate;
    returnOrder = new SeqReorder();
    prefetched = null;
    counters = {
        sessions: 0,
        reconnects: 0,
        returnFrames: 0,
        gatewayKeyframeRequests: 0,
        droppedVideoSends: 0,
        droppedAudioSends: 0,
        withheldFrames: 0,
        keyframeRequests: 0,
        sentVideo: 0,
    };
    lastKeyframeRequestAt = -Infinity;
    /** Refused Hellos since the last Established. */
    refusals = 0;
    lastError = null;
    lastMeasure = null;
    uplinkKbps = 0;
    fps = { capture: 0, encoded: 0, sent: 0 };
    probe = null;
    sendQueueMs = null;
    runPromise = null;
    constructor(opts) {
        this.o = {
            initialBackoffMs: 500,
            maxBackoffMs: 8_000,
            refreshBeforeExpiryMs: 60_000,
            silenceTimeoutMs: 5_000,
            handshakeTimeoutMs: 10_000,
            tickMs: 1_000,
            maxRefusals: 3,
            maxInflightSends: 64,
            maxInflightAudioSends: 16,
            keyframeRequestIntervalMs: 1_000,
            minVideoBitrateBps: 300_000,
            now: () => Date.now(),
            sleep: defaultSleep,
            ...opts,
        };
        this.rate = new RateController({ maxBps: opts.uplink.videoBitrateBps, minBps: this.o.minVideoBitrateBps });
    }
    get connectionState() {
        return this.state;
    }
    start() {
        this.runPromise ??= this.run();
        return this.runPromise;
    }
    async stop() {
        this.stopped = true;
        this.endSession?.('stop');
        await this.runPromise;
        this.setState('ended');
    }
    stats() {
        const now = this.o.now();
        return {
            state: this.state,
            rttMs: this.rate.rttMs === null ? null : Math.round(this.rate.rttMs),
            congested: this.rate.congested(now),
            uplinkKbps: this.uplinkKbps,
            targetKbps: Math.round(this.rate.targetBps / 1000),
            sessions: this.counters.sessions,
            reconnects: this.counters.reconnects,
            returnFrames: this.counters.returnFrames,
            gatewayKeyframeRequests: this.counters.gatewayKeyframeRequests,
            bidirectional: this.session?.bidirectional ?? false,
            lastError: this.lastError,
            ...this.frameStats(now),
        };
    }
    frameStats(now) {
        const u = this.o.uplink.stats;
        const c = this.counters;
        const waiting = this.probe ? now - this.probe.startedAt : null;
        return {
            capturedFrames: u.capturedFrames,
            videoFrames: u.videoFrames,
            audioFrames: u.audioFrames,
            droppedFrames: u.droppedFrames,
            decimatedFrames: u.decimatedFrames,
            droppedSends: c.droppedVideoSends + c.droppedAudioSends,
            droppedVideoSends: c.droppedVideoSends,
            droppedAudioSends: c.droppedAudioSends,
            withheldFrames: c.withheldFrames,
            keyframeRequests: c.keyframeRequests,
            encoderFps: this.o.uplink.encoderFps,
            cameraFps: this.o.uplink.cameraFps,
            captureFps: this.fps.capture,
            encodedFps: this.fps.encoded,
            sentFps: this.fps.sent,
            sendQueueMs: waiting === null ? this.sendQueueMs : Math.max(waiting, this.sendQueueMs ?? 0),
        };
    }
    setState(s) {
        if (this.state === s)
            return;
        this.state = s;
        this.o.onState?.(s);
    }
    async run() {
        let backoff = this.o.initialBackoffMs;
        let first = true;
        while (!this.stopped) {
            this.setState(first ? 'connecting' : 'reconnecting');
            if (!first)
                this.counters.reconnects++;
            first = false;
            let reason = 'error';
            let established = false;
            try {
                const tok = this.prefetched ?? (await this.o.getToken());
                this.prefetched = null;
                if (this.stopped)
                    break;
                const session = await this.o.connector({ url: this.o.url, token: tok.token, certHashes: this.o.certHashes });
                this.counters.sessions++;
                this.session = session;
                await session.beginSession();
                const result = await this.drive(session, tok, () => {
                    established = true;
                    backoff = this.o.initialBackoffMs;
                    this.refusals = 0;
                });
                reason = result;
            }
            catch (err) {
                this.lastError = err instanceof Error ? err.message : String(err);
                if (this.o.shouldStop?.(err)) {
                    this.stopped = true;
                    break;
                }
            }
            finally {
                this.o.uplink.setSink(null);
                const s = this.session;
                this.session = null;
                this.endSession = null;
                if (s)
                    await s.shutdown().catch(() => undefined);
            }
            if (this.stopped || reason === 'stop')
                break;
            this.setState('reconnecting');
            // A refresh or a drain has a token/target ready: go straight back.
            if (reason === 'refresh' || reason === 'drain')
                continue;
            const admitted = await this.stillAdmitted();
            if (this.stopped)
                break;
            if (admitted === false) {
                this.stopped = true;
                break;
            }
            // Count a refusal only when event-manager answered that the guest is
            // admitted: a flaky network at the Hello must not end the session.
            if (reason === 'refused' && admitted === true && ++this.refusals >= this.o.maxRefusals) {
                this.lastError = 'The gateway refused the connection';
                this.stopped = true;
                this.o.onRefused?.();
                break;
            }
            await this.o.sleep(backoff + Math.random() * backoff * 0.2);
            if (!established)
                backoff = Math.min(this.o.maxBackoffMs, backoff * 2);
        }
        this.setState('ended');
    }
    /** `confirmAdmitted`'s answer; null when there is no hook or no answer. */
    async stillAdmitted() {
        if (!this.o.confirmAdmitted)
            return null;
        try {
            return await this.o.confirmAdmitted();
        }
        catch {
            return null;
        }
    }
    /** Run one session until it ends; resolves with why. */
    drive(session, tok, onEstablished) {
        return new Promise((resolve) => {
            let done = false;
            let lastRttAt = 0;
            let live = false;
            let timer = null;
            const handshake = setTimeout(() => {
                this.lastError = 'No response from the gateway; retrying';
                finish('error');
            }, this.o.handshakeTimeoutMs);
            const finish = (r) => {
                if (done)
                    return;
                done = true;
                clearTimeout(handshake);
                if (timer)
                    clearInterval(timer);
                resolve(r);
            };
            this.endSession = finish;
            const tick = async () => {
                const now = this.o.now();
                if (live && lastRttAt > 0 && now - lastRttAt > this.o.silenceTimeoutMs) {
                    this.lastError = 'RTT probes stopped; reconnecting';
                    finish('silent');
                    return;
                }
                if (tok.expiresAtMs !== null && now >= tok.expiresAtMs - this.o.refreshBeforeExpiryMs && !this.prefetched) {
                    try {
                        this.prefetched = await this.o.getToken();
                        finish('refresh');
                    }
                    catch (err) {
                        this.lastError = err instanceof Error ? err.message : String(err);
                        if (this.o.shouldStop?.(err)) {
                            this.stopped = true;
                            finish('stop');
                        }
                    }
                    return;
                }
                this.probeRate(session, now);
                this.o.uplink.setVideoBitrate(this.rate.tick(now));
                this.measureUplink(now);
                if (live && session.bidirectional) {
                    const report = {
                        rttMs: this.rate.rttMs === null ? null : Math.round(this.rate.rttMs),
                        congested: this.rate.congested(now),
                        uplinkKbps: this.uplinkKbps,
                        ...(this.o.audioState ? { audio: this.o.audioState() } : {}),
                        ...this.frameStats(now),
                    };
                    session.sendOnFlow(FLOW_RTT, new TextEncoder().encode(JSON.stringify(report))).catch(() => undefined);
                }
                this.o.onStats?.(this.stats());
            };
            const onEvent = (ev) => {
                switch (ev.kind) {
                    case 'established':
                        clearTimeout(handshake);
                        live = true;
                        lastRttAt = this.o.now();
                        onEstablished();
                        this.rate.resetPath();
                        this.probe = null;
                        this.sendQueueMs = null;
                        this.returnOrder.reset();
                        this.o.returnFeed?.reset();
                        this.o.uplink.setSink(this.sinkFor(session));
                        this.setState('live');
                        timer ??= setInterval(() => void tick(), this.o.tickMs);
                        break;
                    case 'delivered':
                        if (ev.flow === FLOW_RETURN) {
                            for (const p of this.returnOrder.push(ev.seq, ev.payload)) {
                                this.counters.returnFrames++;
                                this.o.returnFeed?.push(p);
                            }
                        }
                        else if (ev.flow === FLOW_CONTROL) {
                            this.onControl(ev.payload);
                        }
                        break;
                    case 'rttSample':
                        lastRttAt = this.o.now();
                        this.rate.onRtt(ev.rttUs / 1000);
                        break;
                    case 'congestionDetected':
                        this.rate.onAvailableRate(ev.availableRateBps, this.o.now());
                        this.o.uplink.setVideoBitrate(this.rate.targetBps);
                        break;
                    case 'drainRequested':
                        finish('drain');
                        break;
                    case 'flowClosed':
                    case 'notice':
                        break;
                }
            };
            (async () => {
                while (!done) {
                    let ev;
                    try {
                        ev = await session.nextEvent();
                    }
                    catch (err) {
                        this.lastError = err instanceof Error ? err.message : String(err);
                        finish(live ? 'error' : 'refused');
                        return;
                    }
                    if (ev === null) {
                        // Closed by the far end. Before Established, the Hello was refused.
                        finish(live ? 'closed' : 'refused');
                        return;
                    }
                    onEvent(ev);
                }
            })();
        });
    }
    /**
     * rift-wt answers `recommendedBitrateBps()` from the same command queue as
     * the sends, so the answer waits behind every send already queued: its
     * latency is the uplink's queueing delay. One probe at a time, and the tick
     * does not wait for it, so a backed-up queue cannot stall the stats or the
     * flow-3 reports that would show it.
     */
    probeRate(session, now) {
        if (this.probe)
            return;
        const probe = { startedAt: now };
        this.probe = probe;
        session.recommendedBitrateBps().then((rec) => {
            if (this.probe !== probe)
                return;
            this.probe = null;
            const at = this.o.now();
            this.sendQueueMs = at - probe.startedAt;
            this.rate.onAvailableRate(rec, at);
            this.o.uplink.setVideoBitrate(this.rate.targetBps);
        }, () => {
            if (this.probe === probe)
                this.probe = null;
        });
    }
    /**
     * Audio and video have separate in-flight budgets. They used to share one,
     * so a video backlog dropped audio and audio held video's slots. Dropping
     * an AAC frame costs that frame and nothing after it.
     *
     * Dropping a video delta breaks every delta after it until the next IDR:
     * JBS feeds NVDEC whatever arrives, so those would decode as smeared
     * garbage. After a drop the deltas are withheld (the receiver holds its
     * last good frame) until a keyframe goes out. A keyframe always goes out.
     * Recovery asks the encoder for one keyframe, at most once per
     * `keyframeRequestIntervalMs`. Before, every drop forced one, and keyframes
     * are large enough to deepen the very backlog that caused the drop.
     *
     * Budgets and the recovery state belong to the session: a new one starts
     * empty, on the keyframe `Uplink.setSink` forces.
     */
    sinkFor(session) {
        const inflight = { video: 0, audio: 0 };
        let awaitingKeyframe = false;
        const send = (p) => {
            inflight[p.kind]++;
            if (p.kind === 'video')
                this.counters.sentVideo++;
            const done = () => inflight[p.kind]--;
            session.sendFrame(p.bytes, p.keyframe).then(done, done);
        };
        return (p) => {
            if (this.session !== session)
                return;
            if (p.kind === 'audio') {
                if (inflight.audio >= this.o.maxInflightAudioSends) {
                    this.counters.droppedAudioSends++;
                    return;
                }
                send(p);
                return;
            }
            if (p.keyframe) {
                awaitingKeyframe = false;
            }
            else if (awaitingKeyframe) {
                this.counters.withheldFrames++;
                this.requestRecoveryKeyframe();
                return;
            }
            else if (inflight.video >= this.o.maxInflightSends) {
                this.counters.droppedVideoSends++;
                awaitingKeyframe = true;
                this.requestRecoveryKeyframe();
                return;
            }
            send(p);
        };
    }
    /**
     * Flow 4. A `keyframeRequest` means the gateway has just routed this guest
     * onto a slot and switches the slot's leg to them at the next frame
     * boundary; the site's decoder needs an IDR to start, and the GOP is 2 s.
     * The encoder's next video frame is an IDR (`encode(frame, { keyFrame:
     * true })`). Unlike recovery it is not rate-limited: the gateway sends one
     * per route change. Other kinds are ignored, so the gateway can add some.
     */
    onControl(payload) {
        if (parseControlMessage(payload)?.kind !== 'keyframeRequest')
            return;
        this.counters.gatewayKeyframeRequests++;
        this.o.uplink.requestKeyframe();
        this.o.onKeyframeRequest?.();
    }
    requestRecoveryKeyframe() {
        const now = this.o.now();
        if (now - this.lastKeyframeRequestAt < this.o.keyframeRequestIntervalMs)
            return;
        this.lastKeyframeRequestAt = now;
        this.counters.keyframeRequests++;
        this.o.uplink.requestKeyframe();
    }
    measureUplink(now) {
        const u = this.o.uplink.stats;
        const cur = { at: now, bytes: u.bytes, captured: u.capturedFrames, encoded: u.videoFrames, sent: this.counters.sentVideo };
        const last = this.lastMeasure;
        if (last && now > last.at) {
            const dt = now - last.at;
            const fps = (n) => Math.round((n * 10_000) / dt) / 10;
            this.uplinkKbps = Math.round(((cur.bytes - last.bytes) * 8) / dt);
            this.fps = {
                capture: fps(cur.captured - last.captured),
                encoded: fps(cur.encoded - last.encoded),
                sent: fps(cur.sent - last.sent),
            };
        }
        this.lastMeasure = cur;
    }
}
//# sourceMappingURL=guest-client.js.map